import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { OrderStateEnums } from 'src/app/enum/order-enums';
import { OrderModel } from 'src/app/models/preliminaries/order.model';
import { OrderPrintModel } from 'src/app/models/print/preliminaries/order-print.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { OrderService } from 'src/app/services/preliminaries/order.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-order-adjustment',
  templateUrl: './register-order-adjustment.component.html',
  styleUrls: ['./register-order-adjustment.component.scss'],
  standalone: false
})
export class RegisterOrderAdjustmentComponent implements OnInit {

  branchId: number = 0;
  companyId: number = 0;
  disableAllDetail: boolean = true;
  disablePartial: boolean = false;
  formOrderAdjustment: FormGroup = new FormGroup({});
  iconState: string = 'pi pi-lock-open';
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  listClients: any[] = [];
  listCostCenters: any[] = [];
  listObservers: Subscription[] = [];
  listSellers: any[] = [];
  listTypes: any[] = [];
  nameWindow: string = 'Captura Ajuste Pedido';
  orderAdjustmentContract: OrderModel = new OrderModel();
  orderAdjustmentId: number = 0;
  orderPrintModel: OrderPrintModel = new OrderPrintModel();
  permissionPrint: boolean = false;
  permissionUpdatable: boolean = true;
  projectName = ProjectName.projectName;
  reloadPrint: boolean = false;
  severityState: string = "danger";
  stateText: string = 'Abierto';
  titleState: string = '';
  url: string = 'order-adjustment';
  hasQuantityInZero: boolean = false;

  constructor(private activatedRoute: ActivatedRoute,
    private branchService: BranchService,
    private dateConverterService: DateConverterService,
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private headerService: HeaderService,
    private location: Location,
    private orderService: OrderService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) {
    this.validateForm(new OrderModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getPermission();
    this.activatedRoute.params.subscribe(params => {
      this.orderAdjustmentId = Object.keys(params).length > 0 ? params?.id : null;
    });
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    this.listObservers.forEach(sub => sub.unsubscribe());
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeBranch(branchId: number, loadAll: boolean) {
    this.branchId = branchId;
    if (loadAll) {
      await this.getAllLists('costCenters', 'sellers');
      this.resetContract();
    } else {
      await this.getAllLists();
    }
  }

  async changeConsecutive(consecutive: number) {
    this.branchId = this.formOrderAdjustment.get("branchId")?.value;
    if (!this.branchId) {
      return this.toastHandlerService.showToast('warning', 'Seleccione una sucursal.');
    }
    if (!Number(consecutive)) {
      return this.resetContract();
    }
    try {
      const response = await firstValueFrom(this.orderService.getOrderByKeysAsync(this.branchId, consecutive));
      this.orderAdjustmentId = response.orderId;
      this.orderAdjustmentContract = response;
      this.validateForm(this.orderAdjustmentContract);
      this.location.replaceState(`/${this.url}/register/${this.orderAdjustmentId}`);
    } catch (error) {
      console.error('Error al buscar el pedido por campos de recuperación.', error);
    }
  }

  async changeState() {
    if (!this.orderAdjustmentId) {
      return this.toastHandlerService.showToast('warning', 'Debe seleccionar un pedido valido para continuar.');
    }
    if (!this.permissionUpdatable) {
      return this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para modificar el estado del pedido.
        Comuníquese con el administrador del sistema.`);
    }
    if (this.orderAdjustmentContract.orderStateId != OrderStateEnums.initialClose) {
      return this.toastHandlerService.showToast('warning', 'El pedido debe estar en Cierre (Inicial) para modificar su estado.');
    }
    try {
      const response = await firstValueFrom(this.orderService.setStateOrder(this.orderAdjustmentId));
      this.orderAdjustmentContract = response;
      this.validateForm(this.orderAdjustmentContract);
      this.toastHandlerService.showToast('success', 'El estado del pedido fue modificado exitosamente.', '');
    } catch (error) {
      console.error('Error al modificar el estado del pedido.', error);
    }
  }

  finishLoading(finishPrint: boolean) {
    if (finishPrint) {
      window.print();
    }
  }

  async getFirstBranchByUserAuthenticated() {
    try {
      const response = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.branchId = response.branchId;
      await this.getAllLists();
      this.resetContract();
    } catch (error) {
      console.error('Error al obtener la sucursal del usuario.', error);
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'clients' | 'sellers' | 'types' | 'costCenters'>) {
    try {
      const lists = {
        branches: async () => {
          this.listBranches = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
        },
        clients: async () => {
          this.listClients = await firstValueFrom(this.orderService.getAllClientsForSelectAsync());
        },
        sellers: async () => {
          this.listSellers = await firstValueFrom(this.orderService.getAllSellersForSelect(this.branchId));
        },
        types: async () => {
          this.listTypes = await firstValueFrom(this.orderService.getAllOrderTypesForSelect());
        },
        costCenters: async () => {
          this.listCostCenters = await firstValueFrom(this.orderService.getAllCostCentersForSelect(this.branchId));
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.costCenters(),
          lists.branches(),
          lists.clients(),
          lists.sellers(),
          lists.types(),
        ]);
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getPermission() {
    this.getUserAuthenticated();
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('warning', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`, 'Atención');
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getPermissionPrint(),
      this.getPermissionProfileByUrl(),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al obtener el permiso actualizable.', error);
    }
  }

  async getPermissionPrint() {
    try {
      const response = await firstValueFrom(this.orderService.getPermissionOrder(OptionWindow.imprimir));
      this.permissionPrint = response;
      if (this.orderAdjustmentId) {
        await this.getOrderById();
      }
    } catch (error) {
      console.error('Error al obtener el permiso de impresión.', error);
    }
  }

  async getOrderById() {
    try {
      const response = await firstValueFrom(this.orderService.getOrderById(this.orderAdjustmentId));
      await this.changeBranch(response.branchId, false);
      this.orderAdjustmentContract = response;
      await this.validateForm(this.orderAdjustmentContract);
    } catch (error) {
      console.error('Error al buscar el pedido.', error);
    }
  }

  async getUserAuthenticated() {
    const load = this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0))
      .subscribe(user => {
        this.companyId = user.companyId;
      });
    this.listObservers.push(load);
  }

  async handleInitialState() {
    if (this.orderAdjustmentId) {
      await this.getOrderById();
    } else {
      await this.getFirstBranchByUserAuthenticated();
    }
  }

  async printPage() {
    if (this.orderAdjustmentContract.orderStateId === OrderStateEnums.open) {
      return this.toastHandlerService.showToast('warning', `Solo es posible imprimir cuando el estado es Cierre inicial o Cierre final.`);
    }
    if (!this.orderAdjustmentId) {
      return this.toastHandlerService.showToast('warning', 'Debe seleccionar un pedido valido para continuar.');
    }
    if (!this.permissionUpdatable) {
      return this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para imprimir pedidos.
        Comuníquese con el administrador del sistema.`);
    }
    try {
      const response = await firstValueFrom(this.orderService.getOrderByKeysByPrintAsync(this.branchId, 0, this.orderAdjustmentId));
      this.orderPrintModel = response;
      this.reloadPrint = !this.reloadPrint;
    } catch (error) {
      console.error('Error al imprimir el pedido.', error);
    }
  }

  async resetContract() {
    this.orderAdjustmentId = 0;
    this.orderAdjustmentContract = new OrderModel();
    this.orderAdjustmentContract.branchId = this.branchId;
    await this.validateForm(this.orderAdjustmentContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  updateFormState(): void {
    const stateConfig: any = {
      [OrderStateEnums.open]: {
        icon: 'pi pi-lock-open',
        title: 'Cierre (Inicial)',
        text: 'Abierta',
        severity: 'danger',
        disablePartial: true,
        disableAllDetail: true,
      },
      [OrderStateEnums.initialClose]: {
        icon: 'pi pi-lock',
        title: 'Cierre (Final)',
        text: 'Cierre (Inicial)',
        severity: '',
        disablePartial: false,
        disableAllDetail: false,
      },
      [OrderStateEnums.finalClose]: {
        icon: 'pi pi-lock',
        title: 'Abrir',
        text: 'Cierre (Final)',
        severity: 'success',
        disablePartial: true,
        disableAllDetail: true,
      },
      [OrderStateEnums.annulled]: {
        icon: 'pi pi-lock',
        title: 'Abrir',
        text: 'Anulado',
        severity: 'warn',
        disablePartial: true,
        disableAllDetail: true,
      }
    };

    const currentState = this.orderAdjustmentContract.orderStateId;
    const config = stateConfig[currentState];
    if (config) {
      this.stateText = config.text;
      this.iconState = config.icon;
      this.titleState = config.title;
      this.severityState = config.severity;
      this.disablePartial = config.disablePartial;
      this.disableAllDetail = config.disableAllDetail;
    }
    if (this.orderAdjustmentId && currentState !== OrderStateEnums.initialClose) {
      this.toastHandlerService.showToast('warning', 'Solo se permiten modificaciones cuando el pedido está en Cierre (Inicial).');
    }
    setTimeout(() => {
      this.formOrderAdjustment.disable();
      this.formControlStateService.setControlState(this.formOrderAdjustment, ['branchId', 'consecutive'], true);
    });
  }

  async validateForm(orderAdjustment: OrderModel) {
    this.formOrderAdjustment = this.formBuilder.group({
      clientId: [orderAdjustment?.costCenter?.clientId],
      advertiser: [orderAdjustment.advertiser],
      consecutive: [orderAdjustment.consecutive],
      purchaseOrderNumber: [orderAdjustment.purchaseOrderNumber],
      orderStateId: [orderAdjustment.orderStateId, [Validators.required]],
      observation: [orderAdjustment.observation, [Validators.maxLength(250)]],
      branchId: [orderAdjustment.branchId, [Validators.required, Validators.min(1)]],
      sellerId: [orderAdjustment.sellerId, [Validators.required, Validators.min(1)]],
      orderTypeId: [orderAdjustment.orderTypeId, [Validators.required, Validators.min(1)]],
      costCenterId: [orderAdjustment.costCenterId, [Validators.required, Validators.min(1)]],
      observationRemission: [orderAdjustment.observationRemission, [Validators.maxLength(250)]],
      dateOrder: [orderAdjustment.dateOrder ? this.dateConverterService.convertUtcToLocalDate(orderAdjustment.dateOrder)
        : null, [Validators.required]],
    })
    this.updateFormState();
  }
}
